import styled from 'styled-components';
import { SearchInput } from '../../components/MainHeader/styles';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { appDispatch, RootState } from '../../store/store';
import { IProjectStore } from '../../store/companies/companiesTypes';
import {
  black,
  blue,
  border1,
  boxShadow1,
  exactWhite,
  gray,
  green,
  lightGray,
  purple,
  red,
} from '../../utils/style/colors';
import NavLink from '../../components/NavLink/NavLink';
import LocalIcon, { LocalIconTypes } from '../../ui/LocalIcon';
import { initialList as tutorialList } from '../../store/siteGuide/guides';
import { IGuide } from '../../store/siteGuide/SiteGuideSlice';
import {
  IProviderItemView,
  IProviderItemViewWithData,
} from '../../store/providers/providerTypes';
import { searchDocuments } from '../../store/providers/providerSlice';
import { searchTasks, updateTask } from '../../store/tasks/tasksSlice';
import {
  IconColorFinder,
  IconLogoFinder,
  ItemCategoryFinder,
} from '../../utils/check-file-type/findIconViaType';
import { LoadingCircleIndicatorNoPad } from '../../components/LoadingCircleIndicator/LoadingCircleIndicator';
import { summerizeText } from '../../utils/summerizeText';
import Icon from '../../components/Icon/Icon';
import SafeLink from '../../components/NavLink/SafeLink';
import { ITask } from '../../store/tasks/tasksType';
import TaskListView from '../../components/TaskListView';
import { display } from '../../components/ErrorView/ErrorDialog';
import UserMixpanel from '../../utils/mixpanel/MixpanelClass';
import { isANumber, preprocessForSearch } from '../../utils/number';
import { navigate } from '@reach/router';
import Typewriter from 'typewriter-effect';
import i18n from 'i18next';
import GuideModal from '../../components/GuideModal/GuideModal';

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  backdrop-filter: blur(2px);
  background: rgba(128, 128, 128, 0.6);
  z-index: 99999999;
  opacity: ${(props) => (props.isActive ? '1' : '0')};
  visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
  transition: all 200ms ease-in-out;
  display: flex;
  color: ${black};
`;

const GeneralSearchContainer = styled.div`
  width: 80vw;
  height: 100vh;
  margin: auto;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 700px) {
    width: 95vw;
    margin: 0 auto;
    padding: 0;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 60rem;
  margin: 0 auto;
  height: 45rem;

  @media screen and (max-width: 700px) {
    width: 100%;
  }
`;

const SearchResultContainer = styled.div`
  width: 100%;
  position: relative;
  border-radius: 1rem;
  background: ${exactWhite};
  overflow: hidden;
  animation: ${(props) =>
    props.isActive ? 'initialAnimation 0.5s ease-out forwards' : 'none'};

  @keyframes initialAnimation {
    0% {
      transform: scale(1.1);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ListCloseContainer = styled.div`
  display: flex;
  gap: 0.5rem;

  @media screen and (max-width: 700px) {
    flex-direction: column-reverse;
  }
`;

const CloseCircle = styled.div`
  // position: relative;
  // left: ${(props) => (props.theme.direction === 'rtl' ? '4rem' : 'auto')};
  // right: ${(props) => (props.theme.direction === 'rtl' ? 'auto' : '4rem')};
  background: ${exactWhite};
  height: 3.5rem;
  min-width: 3.5rem;
  border-radius: 10rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${(props) =>
    props.isActive ? 'slideInFromLeft 1s ease-out' : 'none'};

  @keyframes slideInFromLeft {
    0% {
      transform: translateX(-100px);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideInFromRight {
    0% {
      transform: translateX(-100px);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Search = styled(SearchInput)`
  border-radius: 1rem !important;
  background: #333333;
  border: none !important;
  border-bottom-left-radius: ${(props) =>
    props.hasResult ? '0' : '1rem'} !important;
  border-bottom-right-radius: ${(props) =>
    props.hasResult ? '0' : '1rem'} !important;
  padding: 0 2.75rem !important;

  &:focus {
    border: none !important;
    box-shadow: none !important;
  }

  &:hover {
    border: none !important;
    box-shadow: none !important;
  }
`;

const Result = styled.ul`
  background: var(--exact-white);
  padding: 2rem 1rem 0.5rem 0.5rem;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
`;

const ResultItem = styled.li`
  height: 3rem;
  padding: 0.5rem 2rem;
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const CategoriesedContainer = styled.div`
  background: ${exactWhite};
`;

const CategoriesTitle = styled.div`
  padding-inline-start: 1rem;
  padding-top: 0.5rem;
  color: ${gray};
  background: ${exactWhite};
  font-size: 0.9rem;
  border-bottom: 1px solid ${lightGray};
  padding-bottom: 0.5rem;
`;

const XContainer = styled.div`
  position: absolute;
  top: 0.8rem;
  display: flex;
  flex-direction: row-reverse;
  gap: 0.5rem;
  ${(props) => (props.side === 'left' ? 'left: 8px;' : 'right: 8px')}
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemResult = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: auto auto;
  grid-template-columns: repeat(auto-fill, 200px);
  gap: 1rem;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
  padding: 2rem;
`;

const TaskResult = styled.div`
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-auto-flow: column;
  grid-auto-columns: 30rem;
  background: var(--exact-white);
  padding: 0.5rem 2rem;
  gap: 0 1rem;
  overflow-x: auto;
`;

const TaskResultItem = styled.div`
  transition: all 0.2s ease-in-out;

  & > div {
    box-shadow: ${boxShadow1};
    border-radius: 2rem;
  }

  &:hover {
    transform: scale(1.025);
  }
`;

const ItemResultItem = styled.div`
  min-width: 200px;
  max-width: 200px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  height: 4.5rem;
  border: ${border1};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 1px 1px 5px lightgrey;
    transform: scale(1.05);
  }
`;

export const BadgeContainer = styled.div`
  height: 100%;
  width: 8rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border-radius: 1rem;
  background-image: ${(props) =>
    props.theme.direction === 'rtl'
      ? `linear-gradient(to left, ${props.bgColor} 0%, ${exactWhite} 100%)`
      : `linear-gradient(to right, ${props.bgColor} 0%, ${exactWhite} 100%)`};
  padding-inline-start: 1rem;
  color: ${exactWhite};
`;

// const BadgeContent = styled.div``;
// max-width: calc(100% - 4rem);

export const Content = styled.div`
  height: 100%;
  width: calc(100% - 4rem);
  margin-inline-start: -4rem;
  background-color: rgba(255, 255, 255, 0.5);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 0.75rem;
  padding-inline-start: 0.5rem;
  flex-wrap: wrap;
`;

const SearchIntroductionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1rem;
  gap: 0.5rem;
`;

const SearchIntroductionItem = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;
  background: ${(props) => props.bgColor};
  border-radius: 1.75rem;
  padding: 0.5rem;
`;

const CirclItemContainer = styled.div`
  padding: 1rem;
  border-radius: 1.5rem;
  height: 4rem;
  width: 4rem;
  background: ${(props) => props.bgColor};
`;
interface IProps {
  companyId: number;
  isActive?: boolean;
  onChangeGeneralSearchStatus: (status: boolean) => void;
  providerId?: string;
}

interface ISearchResult {
  name: string;
  tags: string[];
  url: string;
  icon: string;
  isItem?: boolean;
  isTask?: boolean;
  isGuide?: boolean;
  bgColor: string;
  task?: ITask;
  parentUrl?: string;
}

const LIGHT_GREEN = '#00800017';
const LIGHT_RED = '#ff000017';
const LIGHT_PURPLE = '#80008017';
const LIGTH_BLUE = '#0000ff17';

const GeneralSearch: React.FC<IProps> = ({
  companyId,
  isActive = false,
  onChangeGeneralSearchStatus,
  // providerId,
}) => {
  const { t } = useTranslation('general');

  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const availableItemTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const company = useSelector(
    (state: RootState) => state.companies.data[companyId]
  );
  const tasks = useSelector((state: RootState) => state.tasks.data);
  const thisUser = useSelector((state: RootState) => state.thisUser);

  const relatedProviderListIds = Object.keys(company?.relatedProviders || {});
  const providers = useSelector((state: RootState) => state.providers.map);
  const providerItems = useSelector(
    (state: RootState) => state.providers.items
  );

  const selectedProviders = Object.keys(providers)
    .filter((pId) => relatedProviderListIds.includes(pId))
    .map((pId) => providers[pId].data);

  const [searchText, setSearchText] = useState<string>('');
  const [result, setResult] = useState<Array<ISearchResult>>([]);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [isFirstMount, setIsFirstMount] = useState<boolean>(true);
  const taskTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showTyping, setShowTyping] = useState<number>(-1);
  const [selectedGuide, setSelectedGuide] = useState<{
    link: string;
    title: string;
  } | null>(null);

  const hasResult = result.length > 0;

  useEffect(() => {
    const handleCloseSearch = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onChangeGeneralSearchStatus(false);
      }
    };

    const handleCloseSearchESC = (event: any) => {
      if (event.key === 'Escape') {
        onChangeGeneralSearchStatus(false);
      }
    };

    window.addEventListener('mousedown', handleCloseSearch);
    window.addEventListener('keydown', handleCloseSearchESC);

    return () => {
      window.removeEventListener('mousedown', handleCloseSearch);
      window.removeEventListener('keydown', handleCloseSearchESC);
    };
  }, []);

  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      setShowTyping(2);
    }
    if (showTyping <= 1) {
      setShowTyping((prevVal) => prevVal + 1);
    }
  }, [isActive]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (availableItemTimeoutRef.current)
      clearTimeout(availableItemTimeoutRef.current);

    if (searchText.length <= 2 && result.length > 0) {
      setResult([]);
      return;
    }

    const companyProviders = Object.values(providers)
      .filter((item) => item.data?.managingCompanyId === companyId)
      .map((provider) => provider.data);

    const companyProviderIdList = companyProviders.map((el: any) => el.id);

    setLoadingSearch(true);

    if (searchText.length >= 2) {
      availableItemTimeoutRef.current = setTimeout(async () => {
        companyProviderIdList.map((pId: number) => {
          const items = providerItems[pId]?.items;
          Object.values(items).map((el: IProviderItemViewWithData) => {
            el.data.name?.split(' ').map((word: string) => {
              if (
                preprocessForSearch(word).includes(
                  preprocessForSearch(searchText)
                )
              ) {
                const searchedItem: ISearchResult = {
                  name: el.data.name || '',
                  tags: [
                    t(
                      `${ItemCategoryFinder({
                        type: el.data.type,
                        format: el.data?.document?.properties.format,
                        tags: el.data?.tags,
                      })}`
                    ),
                  ],
                  url:
                    el.data.type !== 'folder' && el.data.type !== 'searchTag'
                      ? `${window.location.pathname}?id=${el.data.id}`
                      : `/content/${pId}/f/${el.data.id}`,
                  parentUrl: el.data.dadId
                    ? `/content/${pId}/f/${el.data.dadId}`
                    : `/content/${pId}/f/${el.data.id}`,
                  icon: IconLogoFinder({
                    type: el.data.type,
                    format: el.data?.document?.properties.format,
                    tags: el.data?.tags,
                  }),
                  isItem: true,
                  bgColor: `${IconColorFinder(el.data.type)}`,
                };
                setResult((prevState) => {
                  if (
                    !prevState.some((item) => item.url === searchedItem.url)
                  ) {
                    return [...prevState, searchedItem];
                  }
                  return prevState;
                });
              }
            });
          });
        });
      }, 1000);
    }

    timeoutRef.current = setTimeout(async () => {
      if (searchText.length > 2) {
        try {
          for (const provider of companyProviders) {
            const data = await appDispatch(
              searchDocuments({
                searchTerm: searchText,
                query: searchText,
                providerId: provider?.id as number,
                searchFields: ['name'],
                start: 0,
                count: 100,
                folderId: undefined,
              })
            );

            const searchedItem = data.map((item: IProviderItemView) => ({
              name: item?.name ?? item?.formatedName,
              tags: [
                t(
                  `${ItemCategoryFinder({
                    type: item.type,
                    format: item?.document?.properties.format,
                    tags: item?.tags,
                  })}`
                ),
              ],
              url:
                item.type !== 'folder' && item.type !== 'searchTag'
                  ? `${window.location.pathname}?id=${item.id}`
                  : `/content/${provider!.id}/f/${item.id}`,
              parentUrl: item.dadId
                ? `/content/${provider!.id}/f/${item.dadId}`
                : `/content/${provider!.id}/f/${item.id}`,
              icon: IconLogoFinder({
                type: item.type,
                format: item?.document?.properties.format,
                tags: item?.tags,
              }),
              isItem: true,
              bgColor: `${IconColorFinder(item.type)}`,
            })) as Array<ISearchResult>;

            setResult((prevState) => {
              const newResults: ISearchResult[] = searchedItem.filter(
                (item) =>
                  !prevState.map((el) => el.url).includes(item.url || '')
              );

              return [...prevState, ...newResults];
            });
          }
        } catch (e) {
          console.log(e);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setLoadingSearch(false);
      }
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (availableItemTimeoutRef.current) {
        clearTimeout(availableItemTimeoutRef.current);
      }
      if (taskTimeoutRef.current) {
        clearTimeout(taskTimeoutRef.current);
      }
    };
  }, [searchText]);

  useEffect(() => {
    setTimeout(() => {
      if (isActive && searchRef.current) {
        searchRef.current.focus();
      }
      if (isActive && isFirstMount) {
        setIsFirstMount(false);
      }
    }, 50);
  }, [isActive]);

  const handleSearchContent = (text: string) => {
    setSearchText(text);
    if (text.length > 2) {
      const searchedResult: Array<ISearchResult> = [];
      // searching sidebar / task sidebar
      (Object.values(company?.projects) || []).map((project: IProjectStore) => {
        if (
          preprocessForSearch(project.name).includes(preprocessForSearch(text))
        ) {
          searchedResult.push({
            name: project.name,
            tags: project.tags.length ? project.tags : [`${t('other')}`],
            url: `/t/${companyId}/project/${project.id}/task`,
            icon: 'task.svg',
            isItem: false,
            bgColor: `${exactWhite}`,
          });
        } else if (project.tags?.length) {
          searchedResult.push(
            ...project.tags
              .filter(
                (tag) =>
                  preprocessForSearch(tag).includes(
                    preprocessForSearch(text)
                  ) && !project.archived
              )
              .map(() => ({
                name: project.name,
                tags: project.tags,
                url: `/t/${companyId}/project/${project.id}/task`,
                icon: 'task.svg',
                isItem: false,
                bgColor: `${exactWhite}`,
              }))
          );
        }
      });

      selectedProviders?.forEach((provider) => {
        if (
          provider?.useVirtualSpaces &&
          provider?.managingCompanyId === companyId
        ) {
          Object.values(providerItems[provider.id].items).forEach((item) => {
            if (
              item.data.dadId === provider.rootDocument.id &&
              item.data.name?.toLowerCase().includes(text.toLowerCase())
            ) {
              searchedResult.push({
                name: item.data.name as string,
                tags: [`${t('panel')}`],
                url: `/content/${provider.id}/f/${item.data.id}`,
                icon: 'zip.svg',
                isItem: false,
                bgColor: `${exactWhite}`,
              });
            }
          });
        } else if (
          !provider?.useVirtualSpaces &&
          preprocessForSearch(provider?.name || '').includes(
            preprocessForSearch(text)
          )
        ) {
          searchedResult.push({
            name: provider?.name || '',
            tags: [`${t('panel')}`],
            url: `/content/${provider?.id}`,
            icon: 'zip.svg',
            isItem: false,
            bgColor: `${exactWhite}`,
          });
        }
      });

      // searching through tasks using searchTasks from taskSlice
      if (text.length > 2) {
        appDispatch(
          searchTasks({
            params: {
              companyId,
              text,
              count: 20,
              from: 0,
              archived: false,
              deleteState: 'notDeleted',
            },
          })
        )
          .then(({ tasks: searchedTasks }: { tasks: ITask[] }) => {
            const taskResults = searchedTasks
              .filter((task: ITask) => !task.template)
              .map((task) => {
                const templateName =
                  tasks[task.templateId]?.taskName || t('other');
                return {
                  name: task.title,
                  tags: [templateName],
                  url: `/t/${companyId}/project/${task.projectId}/task?taskId=${task.id}`,
                  icon: 'task.svg',
                  isItem: false,
                  isTask: true,
                  bgColor: `${exactWhite}`,
                  task,
                };
              });

            setResult((prevState) => {
              const filteredResults = prevState.filter((item) => !item.isTask);
              return [...filteredResults, ...taskResults];
            });
          })
          .catch(console.error);
      }

      tutorialList.map((guide: IGuide) => {
        if (
          preprocessForSearch(guide.title).includes(preprocessForSearch(text))
        )
          searchedResult.push({
            name: guide.title,
            tags: [`${t('tutorial')}`],
            url: guide.link,
            icon: 'tutorial.svg',
            isItem: false,
            isGuide: true,
            bgColor: `${exactWhite}`,
          });
      });

      setResult(searchedResult);
    }
  };

  let uniqueTags: Array<string> = [];
  const categorizedTags: Record<string, Array<ISearchResult>> = {};
  if (result.length > 0) {
    uniqueTags = result
      .map((res) => res.tags)
      .reduce((acc: string[], tag) => [...acc, ...tag], [])
      .filter((item) => !item.toLowerCase().startsWith('conf_'));

    uniqueTags.forEach((tag) => {
      categorizedTags[tag] = result.filter((res) => res.tags.includes(tag));
    });
  }

  const handleClickOnTask = (task: ITask, _: any) => {
    const mx = UserMixpanel.getInstance();
    mx.visitedTasksCountHandler();
    if (isANumber(task.id)) {
      mx.handleSetVisitedTaskCountByTemplates(task.taskName);
    }
    let url = window.location.href.includes('home')
      ? `/t/${companyId}/home/editor`
      : `/t/${companyId}/task/editor`;
    if (task.id) {
      url = `${url}/${task.id}/info`;
    }
    navigate(url);
  };

  const handleUpdateTask = (task: ITask) => {
    appDispatch(updateTask({ task })).catch((err: any) => {
      display(err);
    });
  };

  return (
    <Container isActive={isActive}>
      <GeneralSearchContainer>
        <SearchContainer>
          <ListCloseContainer ref={ref} className="flex gap">
            <SearchResultContainer isActive={isActive}>
              <XContainer side="right">
                <Icon
                  type="delete"
                  onClick={() => {
                    setSearchText('');
                    setResult([]);
                  }}
                  color={black}
                  style={{ fontSize: '1.7rem', cursor: 'pointer' }}
                />
              </XContainer>

              <Search
                type="text"
                value={searchText}
                placeholder={t('search')}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleSearchContent(event.target.value);
                }}
                style={{
                  height: '5rem',
                  minHeight: '3.5rem',
                  outline: 'none',
                  borderBottomLeftRadius: hasResult ? '0 !important' : '1rem',
                  borderBottomRightRadius: hasResult ? '0 !important' : '1rem',
                }}
                hasResult={hasResult}
                autoFocus
                ref={searchRef}
              />

              {searchText.length > 0 && (
                <XContainer side="left">
                  {loadingSearch && (
                    <LoadingContainer>
                      <LoadingCircleIndicatorNoPad
                        style={{ margin: 0, padding: 0 }}
                        innerStyle={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </LoadingContainer>
                  )}
                </XContainer>
              )}

              <div
                style={{
                  overflow: 'auto',
                  borderBottomLeftRadius: '1rem',
                  borderBottomRightRadius: '1rem',
                  maxHeight: '40rem',
                }}
              >
                {!hasResult && searchText.length === 0 && (
                  <SearchIntroductionContainer>
                    <SearchIntroductionItem bgColor={LIGHT_GREEN}>
                      <CirclItemContainer bgColor={green}>
                        <LocalIcon
                          iconName={'tutorial.svg'}
                          color={exactWhite}
                        />
                      </CirclItemContainer>
                      {showTyping <= 1 ? (
                        <Typewriter
                          key={isActive ? 'active1' : 'inactive1'}
                          options={{
                            strings: t(
                              'generalSearchTutorialPanelsAndTutorials'
                            ),
                            delay: 10,
                            autoStart: isActive,
                            loop: false,
                          }}
                        />
                      ) : (
                        <div>
                          {t('generalSearchTutorialPanelsAndTutorials')}
                        </div>
                      )}
                    </SearchIntroductionItem>
                    <SearchIntroductionItem bgColor={LIGHT_PURPLE}>
                      <CirclItemContainer bgColor={purple}>
                        <LocalIcon iconName={'survey.svg'} color={exactWhite} />
                      </CirclItemContainer>
                      {showTyping <= 1 ? (
                        <Typewriter
                          key={isActive ? 'active2' : 'inactive2'}
                          options={{
                            strings: t('generalSearchTutorialOfItems'),
                            delay: 10,
                            autoStart: isActive,
                            loop: false,
                          }}
                        />
                      ) : (
                        <div>{t('generalSearchTutorialOfItems')}</div>
                      )}
                    </SearchIntroductionItem>
                    <SearchIntroductionItem bgColor={LIGHT_RED}>
                      <CirclItemContainer bgColor={red}>
                        <LocalIcon iconName={'tasks.svg'} color={exactWhite} />
                      </CirclItemContainer>
                      {showTyping <= 1 ? (
                        <Typewriter
                          key={isActive ? 'active3' : 'inactive3'}
                          options={{
                            strings: t('generalSearchTutorialTasks'),
                            delay: 10,
                            autoStart: isActive,
                            loop: false,
                          }}
                        />
                      ) : (
                        <div>{t('generalSearchTutorialTasks')}</div>
                      )}
                    </SearchIntroductionItem>
                    <SearchIntroductionItem bgColor={LIGTH_BLUE}>
                      <CirclItemContainer bgColor={blue}>
                        <LocalIcon iconName={'zip.svg'} color={exactWhite} />
                      </CirclItemContainer>
                      {showTyping <= 1 ? (
                        <Typewriter
                          key={isActive ? 'active3' : 'inactive3'}
                          options={{
                            strings: t('includeParentFolder'),
                            delay: 10,
                            autoStart: isActive,
                            loop: false,
                          }}
                        />
                      ) : (
                        <div>{t('includeParentFolder')}</div>
                      )}
                    </SearchIntroductionItem>
                  </SearchIntroductionContainer>
                )}

                {hasResult &&
                  Object.entries(categorizedTags).map(([key, value]) => {
                    return (
                      <CategoriesedContainer key={key}>
                        <CategoriesTitle>{key}</CategoriesTitle>

                        {value.some((item) => !item.isItem && !item.isTask) && (
                          <Result>
                            {value
                              .filter((res) => !res.isItem && !res.isTask)
                              .map((res: ISearchResult) => {
                                if (res?.isGuide) {
                                  return (
                                    <ResultItem
                                      key={res.name + key}
                                      onClick={() => {
                                        setSelectedGuide({
                                          link: res.url,
                                          title: res.name,
                                        });
                                        // onChangeGeneralSearchStatus(false);
                                      }}
                                    >
                                      <LocalIcon
                                        iconName={res.icon as LocalIconTypes}
                                        color={black}
                                        type="sm"
                                      />
                                      <p>{res.name}</p>
                                    </ResultItem>
                                  );
                                }
                                return (
                                  <SafeLink to={res.url} key={res.name + key}>
                                    <ResultItem
                                      onClick={() =>
                                        onChangeGeneralSearchStatus(false)
                                      }
                                    >
                                      <LocalIcon
                                        iconName={res.icon as LocalIconTypes}
                                        color={black}
                                        type="sm"
                                      />
                                      <p>{res.name}</p>
                                    </ResultItem>
                                  </SafeLink>
                                );
                              })}
                          </Result>
                        )}

                        {value.some((item) => item.isItem && !item.isTask) && (
                          <ItemResult>
                            {value
                              .filter((res) => res.isItem && !res.isTask)
                              .map((res: ISearchResult) => {
                                return (
                                  <NavLink
                                    title={i18n.t(
                                      'toShowEnclosingFolderUseCtrlClick',
                                      { ns: 'general' }
                                    )}
                                    path={
                                      res.url.includes('?id=')
                                        ? `${window.location.pathname}?${res.url.split('?')[1]}`
                                        : res.url
                                    }
                                    key={res.url}
                                    onMouseDown={(e: any) => {
                                      if (e.ctrlKey || e.metaKey) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        navigate(res.parentUrl!);
                                        onChangeGeneralSearchStatus(false);
                                      }
                                    }}
                                    onContextMenu={(e) => e.preventDefault()}
                                  >
                                    <ItemResultItem
                                      onClick={() =>
                                        onChangeGeneralSearchStatus(false)
                                      }
                                    >
                                      <BadgeContainer bgColor={res.bgColor}>
                                        <LocalIcon
                                          iconName={res.icon as LocalIconTypes}
                                        />
                                      </BadgeContainer>
                                      <Content>
                                        <div
                                          style={{
                                            width: '100%',
                                            textWrap: 'wrap',
                                            paddingInlineEnd: '0.25rem',
                                            wordWrap: 'break-word',
                                          }}
                                        >
                                          {summerizeText(res.name, 37)}
                                        </div>
                                      </Content>
                                    </ItemResultItem>
                                  </NavLink>
                                );
                              })}
                          </ItemResult>
                        )}

                        {value.some((item) => item.isTask) && (
                          <TaskResult>
                            {value
                              .filter((res) => res.isTask && res.task)
                              .sort((a, b) => {
                                return b.task!.order - a.task!.order;
                              })
                              .map((res: ISearchResult) => (
                                <TaskResultItem
                                  key={res.task!.id}
                                  onClick={() =>
                                    onChangeGeneralSearchStatus(false)
                                  }
                                >
                                  <TaskListView
                                    index={res.task!.id}
                                    task={res.task!}
                                    template={
                                      tasks[res.task!.templateId] || res.task!
                                    }
                                    thisUser={thisUser}
                                    companyId={companyId}
                                    thisUserId={Number(thisUser.userId)}
                                    projectId={res.task!.projectId}
                                    onClickOnTask={(e: any) =>
                                      handleClickOnTask(res.task!, e)
                                    }
                                    onUpdateTask={handleUpdateTask}
                                    companyOwnerId={company?.owner?.userId || 0}
                                    windowSize="small"
                                    disableDroppable={true}
                                  />
                                </TaskResultItem>
                              ))}
                          </TaskResult>
                        )}
                      </CategoriesedContainer>
                    );
                  })}
              </div>
            </SearchResultContainer>

            <div className="flex justify-end">
              <CloseCircle
                isActive={isActive}
                onClick={() => onChangeGeneralSearchStatus(false)}
              >
                <LocalIcon iconName={'x.svg'} color={black} />
              </CloseCircle>
            </div>
          </ListCloseContainer>
        </SearchContainer>
      </GeneralSearchContainer>
      <GuideModal
        guideLink={selectedGuide?.link || ''}
        guideTitle={selectedGuide?.title || ''}
        isOpen={!!selectedGuide}
        onClose={() => {
          onChangeGeneralSearchStatus(false);
          setSelectedGuide(null);
        }}
      />
    </Container>
  );
};

export default GeneralSearch;
